// Composer block + blueprint types, labels, and seed catalogs.

import type { Tables } from "@/integrations/supabase/types";

export type ComposerBlockType =
  | "global"
  | "cta"
  | "closing"
  | "signature"
  | "postscript"
  | "quote"
  | "author_bio"
  | "product_cta"
  | "affiliate_cta";

export type ComposerBlockRow = Tables<"composer_blocks">;

export type ComposerBlockImagePosition = "above" | "below" | "only";
export type ComposerBlockImageSize = "small" | "medium" | "large";
export type ComposerBlockImageAlign = "left" | "center";

/** Block types that may carry an optional image (avatar / product image / signature image). */
export const BLOCK_TYPES_WITH_IMAGE: ComposerBlockType[] = [
  "signature",
  "author_bio",
  "product_cta",
];

export type ComposerBlock = {
  id: string;
  type: ComposerBlockType;
  title: string;
  /**
   * Sanitized HTML produced by `BlockBodyEditor` (legacy rows stored plain
   * text; both shapes are handled transparently by the editor on load).
   */
  body: string;
  /** Printed name for Signature blocks — also seeds the typed-signature dialog. */
  signatureName?: string | null;
  imageUrl?: string | null;
  imagePath?: string | null;
  imageAlt?: string | null;
  imagePosition?: ComposerBlockImagePosition | null;
  imageSize?: ComposerBlockImageSize | null;
  imageAlign?: ComposerBlockImageAlign | null;
};

export const BLOCK_TYPE_ORDER: ComposerBlockType[] = [
  "global",
  "cta",
  "closing",
  "signature",
  "postscript",
  "quote",
  "author_bio",
  "product_cta",
  "affiliate_cta",
];

export const BLOCK_TYPE_LABELS: Record<ComposerBlockType, string> = {
  global: "My Global Blocks",
  cta: "CTA Blocks",
  closing: "Closing Blocks",
  signature: "Signature Blocks",
  postscript: "Postscript Blocks",
  quote: "Quote Blocks (non-Excerpt)",
  author_bio: "Author Bio Blocks",
  product_cta: "Product CTA Blocks",
  affiliate_cta: "Affiliate CTA Blocks",
};

export const BLOCK_TYPE_DESCRIPTIONS: Record<ComposerBlockType, string> = {
  global: "Reusable across all your pieces — your most-used phrasings.",
  cta: "Calls to action you drop near the end of a piece.",
  closing: "Closing paragraphs that wrap a piece with intention.",
  signature: "Sign-offs, bylines, and short bios.",
  postscript: "P.S. lines that follow your signature.",
  quote: "Pull-quotes from third parties (use Excerpts for your own library).",
  author_bio: "About-the-author footer blocks with an optional avatar image.",
  product_cta: "Footer CTAs that promote a specific product, with an optional image.",
  affiliate_cta: "Footer block promoting your Excerptus affiliate referral link.",
};


// Default seed content used to populate a workspace on first panel open.
// Keyed by `seed_slug` so re-opening doesn't duplicate.
export const DEFAULT_SEED_BLOCKS: Array<
  Pick<ComposerBlock, "type" | "title" | "body"> & { seed_slug: string }
> = [
  { seed_slug: "global-tagline", type: "global", title: "Brand tagline", body: "Research. Write. Publish — from one source of truth." },
  { seed_slug: "cta-newsletter", type: "cta", title: "Subscribe to the newsletter", body: "If this resonated, the weekly letter goes deeper. Subscribe at [your-link]." },
  { seed_slug: "cta-share", type: "cta", title: "Share with one person", body: "If one person came to mind while you read this, send it their way." },
  { seed_slug: "closing-soft", type: "closing", title: "Soft close", body: "That is the whole of it — small, but the kind of small that compounds." },
  { seed_slug: "closing-question", type: "closing", title: "Closing question", body: "What is one thing you would change about how you approach this?" },
  { seed_slug: "sig-short", type: "signature", title: "Short signature", body: "— [Your name]" },
  { seed_slug: "sig-bio", type: "signature", title: "Signature with one-line bio", body: "— [Your name]\n[One-line bio · link]" },
  { seed_slug: "ps-reply", type: "postscript", title: "P.S. — invite a reply", body: "P.S. Hit reply and tell me what landed. I read every one." },
  { seed_slug: "quote-attrib", type: "quote", title: "Attributed pull-quote", body: "\"[Quote text here.]\"\n— [Author], [Source]" },
];

export type ComposerBlueprintBlockRef = {
  type: ComposerBlockType;
  blockId?: string;
  note?: string;
};

export type ComposerBlueprint = {
  id: string;
  title: string;
  description: string;
  contentTypeHint?: string;
  blockRefs: ComposerBlueprintBlockRef[];
};

// Seed catalog used to populate a workspace's blueprints on first panel open.
export const SEED_BLUEPRINTS: ComposerBlueprint[] = [
  {
    id: "seed-bp-newsletter",
    title: "Standard newsletter",
    description: "Hook → body → CTA → soft close → signature → P.S.",
    contentTypeHint: "newsletter",
    blockRefs: [
      { type: "global", note: "Opening hook / tagline" },
      { type: "cta" },
      { type: "closing" },
      { type: "signature" },
      { type: "postscript" },
    ],
  },
  {
    id: "seed-bp-essay",
    title: "Short-form essay",
    description: "Pull-quote opener → body → closing question → short signature.",
    contentTypeHint: "essay",
    blockRefs: [
      { type: "quote" },
      { type: "closing" },
      { type: "signature" },
    ],
  },
  {
    id: "seed-bp-share",
    title: "Share-and-close",
    description: "Body → share CTA → soft close → signature.",
    blockRefs: [
      { type: "cta" },
      { type: "closing" },
      { type: "signature" },
    ],
  },
];
